import { Box, Card, LinearProgress, Typography } from "@mui/material";
import { keyframes } from "@mui/system";
import authTopoBgMobile from "assets/images/auth_topo_bg.png";
import authTopoBgDesktop from "assets/images/BgImage.webp";
import inkfreeMark from "assets/images/inkfree_collasped_logo.svg";
import { useAppSelector } from "hooks/use-app-select";
import AuthLayout from "layouts/AuthLayout/AuthLayout";
import { RootState } from "utils/store";
import { signerAccentBar, signerBackdrop } from "theme/colors";

// Gentle "breathing" pulse for the Inkfree mark while the request loads.
const brandPulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.7; }
  50% { transform: scale(1.08); opacity: 1; }
`;

interface CustomProps {
  children: any;
  minWidth?: string;
  maxWidth?: string;
  IsCustomAuthCardLayout?: boolean;
  /** When true, render the content directly on the background (no white card).
   * Used for the participant "pending" landing which sits on the orange
   * gradient. Defaults to false so message/status screens keep a readable
   * white card. */
  noCard?: boolean;
  /** Swap the orange auth gradient for the "Signer Experience" backdrop —
   * deep ink with purple/coral glows. Opt-in so the auth, onboarding and 404
   * screens sharing this layout keep the orange gradient. */
  signerTheme?: boolean;
}

const CustomAuthCardLayout = (props: CustomProps) => {
  const { loading } = useAppSelector((state: RootState) => state.app);
  const signerTheme = props.signerTheme ?? false;
  return (
    <Box
      sx={{
        position: "relative",
        minHeight: "100vh",
        backgroundColor: signerTheme ? signerBackdrop.color : undefined,
        backgroundImage: signerTheme ? signerBackdrop.image : undefined,
        background: signerTheme
          ? undefined
          : "linear-gradient(90deg, #F26A2E 0%, #EC5560 100%)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        textAlign: "center",
        overflowX: "hidden",
      }}
    >
      {/* White topographic band across the top — fixed height so it reads the
       * same on both mobile and wide desktop screens (a CSS "100% auto"
       * background would balloon this band on wide viewports). The signer
       * backdrop is a flat wash, so the band is dropped there. */}
      {!signerTheme && (
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: { xs: 220, sm: 260, md: 320 },
            backgroundColor: "#ffffff",
            backgroundImage: {
              xs: `url(${authTopoBgMobile})`,
              md: `url(${authTopoBgDesktop})`,
            },
            backgroundRepeat: "no-repeat",
            backgroundPosition: "top center",
            backgroundSize: "cover",
            zIndex: 0,
          }}
        />
      )}
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          flex: "1 1 auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <AuthLayout
          minWidth={props.minWidth}
          maxWidth={props.maxWidth}
          IsCustomAuthCardLayout={props.IsCustomAuthCardLayout}
        >
          {loading ? (
            // Show the white document-card placeholder (with the loader inside)
            // from the very first render, using the SAME wrapper/metrics as the
            // DocumentPreview box so there is no position shift when fetching
            // starts and the real box takes over.
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                overflowX: "hidden",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  maxWidth: 520,
                  mx: "auto",
                  borderRadius: "18px",
                  overflow: "hidden",
                  boxShadow: "0 24px 70px rgba(0,0,0,0.28)",
                  backgroundColor: "background.paper",
                }}
              >
                <Box
                  aria-hidden
                  sx={{ height: "5px", background: signerAccentBar }}
                />
                {/* thin indeterminate progress line under the accent ribbon */}
                <LinearProgress
                  sx={{
                    height: 3,
                    backgroundColor: "signer.orange.tint",
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: "primary.main",
                    },
                  }}
                />
                <Box
                  sx={{
                    minHeight: 300,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 2,
                    p: 4,
                  }}
                >
                  <Box
                    component="img"
                    src={inkfreeMark}
                    alt="Inkfree"
                    sx={{
                      width: 54,
                      height: "auto",
                      animation: `${brandPulse} 1.5s ease-in-out infinite`,
                    }}
                  />
                  <Typography
                    sx={{
                      color: "signer.text.meta",
                      fontWeight: 500,
                      fontSize: "0.95rem",
                    }}
                  >
                    Loading…
                  </Typography>
                </Box>
              </Box>
            </Box>
          ) : props.noCard ? (
            <Box sx={{ position: "relative", width: "100%" }}>
              {props.children}
            </Box>
          ) : (
            <Box sx={{ position: "relative" }}>
              <Card
                elevation={0}
                sx={{
                  padding: { xs: "20px", sm: "32px" },
                  display: "flex",
                  flexDirection: "column",
                  background: "#ffffff",
                  border: "2px solid #EC5560",
                  borderRadius: "16px",
                  boxShadow: "0 12px 32px rgba(0, 0, 0, 0.18)",
                  position: "relative",
                  zIndex: 1,
                }}
              >
                {props.children}
              </Card>
            </Box>
          )}
        </AuthLayout>
      </Box>
    </Box>
  );
};

export default CustomAuthCardLayout;
